import React, { useCallback, useState } from "react";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { BulkUpdateTransactionsRequestInput } from "@common/schemas/transaction.schema";
import { Transaction } from "@common/types/transaction";
import { TransactionView } from "@common/types/views/transactionViews";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useClasses } from "@api/class.api";
import { HStack } from "@design-system/HStack/HStack";
import rulesCreationToastStyles from "../../transactionRules/RuleCreationToast/RuleCreationToast.module.scss";
import {
    BulkUpdateDetails,
    BulkUpdateMode,
    BulkUpdateParams,
    BulkUpdateStatus,
    preparePayloadsForFilters,
} from "../../transactions/TransactionsBulkActions/lib";
import { TransactionsUpdateToast } from "../../transactions/TransactionsBulkActions/TransactionsUpdateStatus/TransactionsUpdateToast";
import { TOAST_MESSAGE_KEY } from "../../transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider.lib";
import { BulkUpdateResult } from "../../transactions/PaginatedTransactionsTable.context";
import { BulkUpdateFailureCounts } from "../../transactions/TransactionsBulkActions/bulkUpdateFailure";
import { SelectedTransactionBase } from "../../transactions/TransactionsBulkActions/transactionsBulkActionsContext";
import { useOverlays } from "../../../contexts/OverlayContext";
import { CategoryIcon } from "../../transactions/CategoryIcon";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import styles from "./SimilarTransactionsUpdateToast.module.scss";
import {
    getSimilarTransactionsRuleCopy,
    getSimilarTransactionsRuleToastAction,
    getSimilarTransactionsUpdateFailureCopy,
    getSimilarTransactionsUpdateValueLabel,
    SIMILAR_TRANSACTIONS_UPDATE_TOAST_MESSAGE_KEY,
} from "./SimilarTransactionsUpdateToast.lib";
import { useAccountLabelForValueToAssign } from "./useAccountLabelForValueToAssign";

interface Props {
    displayedChangedEntityName?: string;
    totalTransactions: number;
    valueToAssign: BulkUpdateParams;
    filters: TransactionFiltersDto;
    view: TransactionView;
    updatedTransaction: Transaction;
    doUpdate?: (
        details: BulkUpdateDetails,
        payloads: BulkUpdateTransactionsRequestInput[],
    ) => Promise<BulkUpdateResult>;
    undo?: (params: {
        previousTransactions: Record<number, SelectedTransactionBase>;
        updateDetailsForUndo: BulkUpdateDetails;
    }) => unknown;
}

export const NameForValueToAssign: React.FC<{
    valueToAssign: BulkUpdateParams;
    displayedChangedEntityName?: string;
    useBold?: boolean;
}> = ({ valueToAssign, displayedChangedEntityName, useBold = false }) => {
    const categoriesById = useCategoriesById();
    const { classesMap } = useClasses();
    const resolvedLabel = useAccountLabelForValueToAssign(valueToAssign);

    if (valueToAssign.update === "categoryId") {
        return (
            <span className={classNames({ "font-weight-medium": useBold })}>
                <CategoryIcon categoryId={valueToAssign.value} />{" "}
                {categoriesById[valueToAssign.value]?.label ?? ""}
            </span>
        );
    }
    if (
        valueToAssign.update === "counterpartyId" ||
        valueToAssign.update === "customerId" ||
        valueToAssign.update === "coa" ||
        valueToAssign.update === "accrualCoa"
    ) {
        let fallbackName: string | undefined;
        if (valueToAssign.update === "counterpartyId") {
            fallbackName = valueToAssign.counterpartyName;
        } else if (valueToAssign.update === "customerId") {
            fallbackName = valueToAssign.customerName;
        } else {
            fallbackName = resolvedLabel ?? valueToAssign.accountName;
        }
        const displayedName = displayedChangedEntityName ?? fallbackName;

        return (
            <span className={classNames({ "font-weight-medium": useBold })}>
                {displayedName}
            </span>
        );
    }
    if (valueToAssign.update === "classes") {
        const numberOfClasses = valueToAssign.value.length;
        const classesLabel = pluralize("Class", numberOfClasses);

        return (
            <span className={classNames({ "font-weight-medium": useBold })}>
                {classesLabel + " "}
                <ClassAvatarGroup
                    className={styles.classList}
                    classes={valueToAssign.value.flatMap((id) => {
                        const classItem = classesMap.get(id);
                        return classItem ? [classItem] : [];
                    })}
                    max={3}
                />
            </span>
        );
    }
    if (valueToAssign.update === "entity") {
        return (
            <span className={styles.entityIcon}>
                <span className={classNames({ "font-weight-medium": useBold })}>
                    Entity{" "}
                    <span className={styles.entityIconContent}>
                        <EntityAvatar
                            size="xs"
                            entity={valueToAssign.value}
                            aria-hidden
                        />
                    </span>
                    {" " + valueToAssign.value.name}
                </span>
            </span>
        );
    }
    return "";
};

export const SimilarTransactionsUpdateResultToast: React.FC<{
    totalTransactions: number;
    updatedValueCount: number;
    ruleCount: number;
    persistedRuleIds: string[];
    valueToAssign: BulkUpdateParams;
    singleValueToAssign?: BulkUpdateParams;
    updateFailure?: BulkUpdateFailureCounts;
}> = ({
    totalTransactions,
    updatedValueCount,
    ruleCount,
    persistedRuleIds,
    valueToAssign,
    singleValueToAssign,
    updateFailure,
}) => {
    const navigate = useStateAwareNavigate();
    const valueLabel = getSimilarTransactionsUpdateValueLabel({
        update: valueToAssign.update,
        count: updatedValueCount,
    });
    const ruleCopy = getSimilarTransactionsRuleCopy({ ruleCount });
    const ruleToastAction =
        getSimilarTransactionsRuleToastAction(persistedRuleIds);
    const updatedValue = singleValueToAssign ? (
        <NameForValueToAssign valueToAssign={singleValueToAssign} useBold />
    ) : (
        <b>
            {updatedValueCount} {valueLabel}
        </b>
    );

    return (
        <div className="transactions-update-toast">
            <div className="transactions-update-toast__content">
                <p className="transactions-update-toast__label">
                    {updateFailure ? (
                        <>
                            {getSimilarTransactionsUpdateFailureCopy({
                                update: valueToAssign.update,
                                failure: updateFailure,
                            })}
                            {ruleCount > 0 &&
                                `. Created ${pluralizeWithCount("rule", ruleCount)}`}
                        </>
                    ) : (
                        <>
                            <b>{totalTransactions}</b> transactions updated to{" "}
                            {updatedValue} {ruleCopy}
                        </>
                    )}
                </p>
            </div>
            {ruleToastAction && (
                <div className="transactions-update-toast__actions">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => navigate(ruleToastAction.path)}
                    >
                        {ruleToastAction.label}
                    </Button>
                </div>
            )}
        </div>
    );
};

export const SimilarTransactionsUpdateToast: React.FC<Props> = ({
    totalTransactions,
    valueToAssign,
    filters,
    doUpdate,
    undo,
    displayedChangedEntityName,
    view,
    updatedTransaction,
}) => {
    const { openOverlay } = useOverlays();

    const [isAssigned, setIsAssigned] = useState(false);

    const { toast, dismiss } = useToaster();

    // NOTE: unhide quick assign once we feel comfortable
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleAssign = useCallback(async () => {
        if (!doUpdate || !undo) {
            return;
        }
        setIsAssigned(true);
        const newDetails: BulkUpdateDetails = {
            status: BulkUpdateStatus.UPDATING,
            transactionsCount: totalTransactions,
            mode: BulkUpdateMode.FILTERS,
            ...valueToAssign,
        };
        const payloads = preparePayloadsForFilters({
            updateParams: newDetails,
            filters,
            transactionCount: totalTransactions,
        });
        const result = await doUpdate(newDetails, payloads);
        toast(
            <TransactionsUpdateToast
                updateDetails={{
                    ...newDetails,
                    status: BulkUpdateStatus.SUCCESS,
                    transactionsCount: result.updatedTransactions.length,
                }}
                displayedChangedEntityName={displayedChangedEntityName}
                previousTransactions={result.transactionsBeforeUpdate}
                undo={undo}
            />,
            TOAST_MESSAGE_KEY,
        );
    }, [
        totalTransactions,
        valueToAssign,
        filters,
        doUpdate,
        toast,
        undo,
        displayedChangedEntityName,
    ]);

    const onReviewClick = useCallback(() => {
        openOverlay("similarTransactionsUpdate", {
            filters,
            valueToAssign,
            displayedChangedEntityName,
            view,
            updatedTransaction,
            totalTransactions,
        });
        dismiss(SIMILAR_TRANSACTIONS_UPDATE_TOAST_MESSAGE_KEY);
    }, [
        openOverlay,
        filters,
        valueToAssign,
        dismiss,
        displayedChangedEntityName,
        view,
        updatedTransaction,
        totalTransactions,
    ]);

    const name = (
        <NameForValueToAssign
            valueToAssign={valueToAssign}
            displayedChangedEntityName={displayedChangedEntityName}
            useBold
        />
    );

    return (
        <div className={rulesCreationToastStyles.message}>
            {isAssigned ? (
                <> Assigning {name}…</>
            ) : (
                <>
                    <p>
                        Kick found{" "}
                        <span className="font-weight-medium">
                            {totalTransactions}
                        </span>{" "}
                        similar transactions. Update to {name} and confirm rule?
                    </p>

                    <footer className={rulesCreationToastStyles.footer}>
                        <HStack gap={12}>
                            <WithHotkey
                                hotKey="alt+enter"
                                hotKeyOptions={{
                                    description: "Review similar transactions",
                                }}
                                hotKeyDisplayPosition="inline"
                            >
                                {(hotkeyHint) => (
                                    <Button
                                        variant="primary"
                                        size="md"
                                        onClick={onReviewClick}
                                        className={
                                            rulesCreationToastStyles.review
                                        }
                                        rightIcon={hotkeyHint}
                                    >
                                        Review
                                    </Button>
                                )}
                            </WithHotkey>
                        </HStack>
                    </footer>
                </>
            )}
        </div>
    );
};
